import { DEFAULT_PROJECT_COLOR_HEX } from '@/modules/projects/constants/project-color';

import { AssignmentStatus } from '../../../types/assignment-status';
import { hexToRGBA } from './helpers';

const DISABLED_OPACITY = 0.3;
const RESIZING_OPACITY = DISABLED_OPACITY;
const CUTTING_OPACITY = 0.6;
const FULL_OPACITY = 1;
const DRAFT_OPACITY = 0.2;
const UNCONFIRMED_OPACITY = 0.12;
const UNCONFIRMED_OPACITY_DARK_MODE = 0.35;

interface OpacityCalculationParams {
  isResizing: boolean;
  isDragging: boolean;
  showPhantom: boolean;
  disabled: boolean;
  isFilteredOut: boolean;
  isCutting: boolean;
  isDuplicating: boolean;
}

export const calculateAssignmentOpacity = ({
  isResizing,
  isDragging,
  showPhantom,
  disabled,
  isFilteredOut,
  isCutting,
  isDuplicating,
}: OpacityCalculationParams): number => {
  // Only apply resizing opacity when phantom is visible (actual drag/resize happening)
  if (showPhantom && (isResizing || isDragging)) return RESIZING_OPACITY;
  if (disabled || isFilteredOut) return RESIZING_OPACITY;
  if (isCutting || isDuplicating) return CUTTING_OPACITY;
  return FULL_OPACITY;
};

interface BackgroundColorParams {
  color: string;
  status: AssignmentStatus;
  isActuallyDark: boolean;
  calculatedOpacity: number;
}

export const getAssignmentBackgroundColor = ({
  color,
  status,
  isActuallyDark,
  calculatedOpacity,
}: BackgroundColorParams): string => {
  const projectBaseColor = color ?? DEFAULT_PROJECT_COLOR_HEX;
  const opacity = calculatedOpacity !== FULL_OPACITY ? calculatedOpacity : null;

  if (status === AssignmentStatus.Draft) {
    return hexToRGBA(projectBaseColor, opacity ?? DRAFT_OPACITY);
  }

  if (status === AssignmentStatus.Unconfirmed && isActuallyDark) {
    return hexToRGBA(projectBaseColor, UNCONFIRMED_OPACITY_DARK_MODE);
  }

  if (status === AssignmentStatus.Unconfirmed) {
    return hexToRGBA(projectBaseColor, opacity ?? UNCONFIRMED_OPACITY);
  }

  return hexToRGBA(projectBaseColor, calculatedOpacity);
};

interface PhantomBackgroundParams {
  color: string;
  status: AssignmentStatus;
}

export const getPhantomBackgroundColor = ({ color, status }: PhantomBackgroundParams): string => {
  const projectBaseColor = color ?? DEFAULT_PROJECT_COLOR_HEX;

  if (status === AssignmentStatus.Draft) {
    return hexToRGBA(projectBaseColor, DRAFT_OPACITY);
  }

  if (status === AssignmentStatus.Unconfirmed) {
    return hexToRGBA(projectBaseColor, UNCONFIRMED_OPACITY);
  }

  return hexToRGBA(projectBaseColor, FULL_OPACITY);
};

export const getAssignmentTextColor = (status: AssignmentStatus, color: string): string => {
  return status === AssignmentStatus.Confirmed ? 'white' : color;
};
